import React, { useEffect, useRef } from 'react';
import styles from './PrivacyContent.module.css';
import backIcon from "../icons/back_icn.png";

const PrivacyContent = ({ onBack }) => {
  const contentRef = useRef(null);

  useEffect(() => {
    const content = contentRef.current;

    if (content) {
      let scrollStep = 1;
      let scrollInterval;

      const startAutoScroll = () => {
        scrollInterval = setInterval(() => {
          if (content.scrollTop + content.clientHeight >= content.scrollHeight) {
            clearInterval(scrollInterval);
          } else {
            content.scrollTop += scrollStep;
          }
        }, 50);
      };

      startAutoScroll();

      const stopAutoScroll = () => {
        clearInterval(scrollInterval);
        content.removeEventListener('wheel', stopAutoScroll);
        content.removeEventListener('touchstart', stopAutoScroll);
      };

      content.addEventListener('wheel', stopAutoScroll);
      content.addEventListener('touchstart', stopAutoScroll);

      return () => {
        content.removeEventListener('wheel', stopAutoScroll);
        content.removeEventListener('touchstart', stopAutoScroll);
        clearInterval(scrollInterval);
      };
    }
  }, []);

  return (
    <div className={styles.legalContentContainer}>
      <div className={styles.legalContent} ref={contentRef}>
	<h1>Privacy Policy</h1>
<p><strong>Effective Date:</strong> April 30, 2026</p>

<h2>Introduction</h2>
<p>
  At Quality Performance Core Ltd ("Quality Performance Core", "qpcore", "we", "us", or "our"), we respect your privacy and strive to collect as little personal information as possible. This Privacy Policy explains how we handle your information when you access our digital service, which provides non-exclusive usage rights to Beats as an intangible service.
</p>
<p>
  We do not rely on email addresses or other direct contact details. Instead, authentication is managed through secure, key-based methods that do not require traditional personal data.
</p>

<h2>Consent to Data Processing</h2>  
<p>
  By using our service, you expressly consent to the processing of any data submitted to our system (including payment information and support requests) in accordance with this Privacy Policy.
</p>

<h2>Information We Collect</h2>
<p>
  <strong>User Identification:</strong> To enable access to your purchased Beats, we may assign or utilize a user ID (e.g., a unique key). This ID is not your real name or email; it’s a minimal technical identifier to grant you access to content you have the rights to use.
</p>
<p>
  <strong>Full Name at License Generation:</strong> When you generate a license for a Beat you’ve acquired, we request your full name solely to include it within the license document. This name is used only in the license text and is not stored or reused for any other purpose.
</p>
<p>
  <strong>Usage Data:</strong> We may collect non-personal usage data, such as which Beats are viewed or accessed. This information helps us understand platform performance and user preferences, enabling us to improve the overall experience.
</p>
<p>
<strong>Third-Party Tracking:</strong> We do not load client-side tracking pixels. Platform performance is measured only through server-side events sent to Meta (Facebook) via the Conversions API. The data transmitted are limited to event name, timestamp, IP address and User-Agent. TikTok and Twitter (X) tracking are currently disabled; no email, phone number or other identifiers are included.
</p>
<p>
These data points are used for aggregated analysis and are not linked to personally identifiable information.
</p>

<h2>Cookies</h2>
<p>
  Our site sets no cookies of its own. During checkout Paddle places
  strictly necessary cookies (for example “paddle_session”) to complete
  payment and prevent fraud. These cookies are essential, expire
  automatically, and are not used for marketing or analytics.
</p>

<h2>Hidden Email Alias</h2>
<p>
  For each user, our system generates a unique hidden email alias linked to their user ID. We use this alias exclusively to receive successful payment notifications from Paddle, to handle update notifications and support feedback, and to automatically forward all messages to your account’s Your Own Stuff → Support section. Your actual email address is neither stored nor used directly.
</p>

<h2>How We Use Your Information</h2>
<p>
  <strong>To Provide Access:</strong> We use your user ID to verify that you are entitled to access certain Beats. This verification is done through key-based authentication methods, ensuring minimal personal data involvement.
</p>
<p>
  <strong>To Improve Services:</strong> We analyze aggregated, non-personal usage data and data collected through third-party tracking tools to enhance platform functionality and user experience.
</p>
<p>
<strong>For Marketing and Analytics:</strong> We do not load client-side pixels. Platform performance is analysed only through server-side events sent to Meta (Facebook) via the Conversions API. No data are passed to TikTok or Twitter (X) at this time.
</p>
<p>
<strong>Minimal Communication:</strong> Since we do not require an email address or other direct contact details, we do not send mass promotional emails. Any updates or important information would be provided within the platform interface itself.
</p>

<h2>Payment Processing</h2>
<p>
  All payments are processed through the Paddle platform. We receive payment confirmations via automated notifications to the hidden email alias and deliver them to your support section in your account.
</p>

<h2>Information Sharing</h2>
<p>
  We do not sell, rent, or trade personal information. We may share non-personally identifiable information with trusted service providers (e.g., for hosting or delivering digital content) strictly to operate and maintain the platform.
</p>

<h2>Data Security</h2>
<p>
  We implement reasonable security measures to protect any minimal data involved. Since we do not handle sensitive personal data like emails or payment card details ourselves, the risk is inherently limited.
</p>

<h2>Data Subject Rights</h2>
<p>
  We do not store your biometric data (fingerprints, Face ID templates, Windows Hello credentials, etc.) on our servers. Instead, we store only a public key (or credential ID) linked to your user account; the actual biometric templates remain securely on your device and are used solely to unlock that key. If you remove the credential from your device, you will no longer be able to authenticate, as no matching key will be available on our side.
</p>
<p>
  To request deletion of any data — including Beat licenses, support conversations, or your entire account — you must be logged in and submit your request via Your Own Stuff → Support section. This ensures we can securely identify your user ID and process the deletion within one week.
</p>

<h2>Data Retention</h2>
<p>
  Your authentication credentials are retained as long as your account remains active. Beat licenses are granted for a term of ten (10) years from the Effective Date and remain available in your personal account. After this period, you must either renew your license or remove the track from all platforms, in accordance with the license terms: “The Term of this Agreement shall be ten (10) years, expiring on the ten (10) year anniversary of the Effective Date.”
</p>

<h2>Data Breach Notification</h2>
<p>
  In the event of a personal data breach, we will notify affected users and relevant regulatory authorities within 72 hours of becoming aware of the breach, in accordance with applicable law.
</p>

<h2>Children's Privacy</h2>
<p>
  Our platform is not intended for users under 13 years of age. We do not knowingly collect personal information from children.
</p>

<h2>Changes to This Policy</h2>
<p>
  We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated effective date, and such changes become effective immediately upon posting.
</p>

<h2>Contact Us</h2>
<p>
  If you have any questions or concerns regarding this Privacy Policy, please contact us at contact@qpcore.com.
</p>
      </div>
      <img
        src={backIcon}
        alt="Back"
        className={`${styles.backIcon} ${styles.visible}`}
        onClick={onBack}
      />
    </div>
  );
};

export default PrivacyContent;
